import { Flame, Zap, Crown, Star } from 'lucide-react';

// ENGAGE-005: Daily Reading Streak Badge — milestone badges earned at 7, 14, 30, 100 days.
// Shows the highest earned badge. Displayed on user profiles and the sidebar.

interface StreakBadgeProps {
  currentStreak: number;
  longestStreak: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const MILESTONES = [
  { days: 100, label: 'Legend', icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/15', ring: 'ring-amber-500/30' },
  { days: 30, label: 'Devoted', icon: Star, color: 'text-purple-400', bg: 'bg-purple-500/15', ring: 'ring-purple-500/30' },
  { days: 14, label: 'Committed', icon: Zap, color: 'text-blue-400', bg: 'bg-blue-500/15', ring: 'ring-blue-500/30' },
  { days: 7, label: 'On Fire', icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/15', ring: 'ring-orange-500/30' },
] as const;

function getMilestone(streak: number) {
  return MILESTONES.find((m) => streak >= m.days) ?? null;
}

const sizeClasses = {
  sm: { wrapper: 'gap-1 px-2 py-0.5', icon: 'h-3 w-3', text: 'text-[10px]', streak: 'text-[10px]' },
  md: { wrapper: 'gap-1.5 px-2.5 py-1', icon: 'h-3.5 w-3.5', text: 'text-xs', streak: 'text-xs' },
  lg: { wrapper: 'gap-2 px-3 py-1.5', icon: 'h-4 w-4', text: 'text-sm', streak: 'text-sm' },
};

export function StreakBadge({ currentStreak, longestStreak, size = 'md', showLabel = true }: StreakBadgeProps) {
  // Award badge based on longest streak (don't lose badge if streak breaks)
  const milestone = getMilestone(longestStreak);
  if (!milestone) return null;

  const Icon = milestone.icon;
  const s = sizeClasses[size];

  return (
    <div
      className={`inline-flex items-center rounded-full ring-1 ${milestone.bg} ${milestone.ring} ${s.wrapper}`}
      title={`${milestone.label} — ${longestStreak}-day best streak${currentStreak > 0 ? `, ${currentStreak}-day current` : ''}`}
    >
      <Icon className={`${s.icon} ${milestone.color}`} />
      {showLabel && (
        <span className={`font-semibold ${milestone.color} ${s.text}`}>
          {milestone.label}
        </span>
      )}
      {currentStreak > 0 && (
        <span className={`font-bold text-text-primary ${s.streak}`}>
          {currentStreak}d
        </span>
      )}
    </div>
  );
}

// Compact badge for use in feed cards, comments, etc.
export function StreakBadgeInline({ streak }: { streak: number }) {
  if (streak < 7) return null;
  const milestone = getMilestone(streak);
  if (!milestone) return null;
  const Icon = milestone.icon;

  return (
    <span
      className={`inline-flex items-center gap-0.5 ${milestone.color}`}
      title={`${milestone.label} — ${streak}-day streak`}
    >
      <Icon className="h-3 w-3" />
    </span>
  );
}
